import jsurl from 'jsurl';

import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
} from 'common/widgets/date-range-picker/models/presets';

import {getReportUrl} from './index';

/** TODO move to insights and/or type properly using insights */

const PROPERTY_TYPE_MAP = {
  STRING: `string`,
  NUMBER: `number`,
  DATE: `datetime`,
  BOOLEAN: `boolean`,
  LIST: `list`,
  COHORT: `list`,
};

const MATH_ACTION_MAP = {
  TOTAL: `total`,
  UNIQUE: `unique`,
  AVERAGE: `average`,
  MEDIAN: `median`,
  MIN: `min`,
  MAX: `max`,
};

const TIME_UNIT_MAP = {
  HOUR: `hour`,
  DAY: `day`,
  WEEK: `week`,
  MONTH: `month`,
  QUARTER: `quarter`,
};

const PROPERTY_SOURCE_MAP = {
  EVENTS: `events`,
  PEOPLE: `people`,
};

const PROPERTY_FILTERS_OPERATOR_MAP = {
  AND: `all`,
  OR: `any`,
};

const STRING_FILTER_OPERATOR_MAP = {
  CONTAINS: `contains`,
  DOES_NOT_CONTAIN: `does not contain`,
  EQUALS: `equals`,
  DOES_NOT_EQUAL: `does not equal`,
  SET: `is set`,
  NOT_SET: `is not set`,
};

const NUMBER_FILTER_OPERATOR_MAP = {
  BETWEEN: `is between`,
  GREATER_THAN: `is greater than`,
  LESS_THAN: `is less than`,
  EQUAL_TO: `is equal to`,
};

const ABSOLUTE_DATE_FILTER_OPERATOR_MAP = {
  ON: `was on`,
  BETWEEN: `was between`,
};

const RELATIVE_DATE_FILTER_OPERATOR_MAP = {
  LESS_THAN: `was in the`,
  GREATER_THAN: null, // from segmentation, but not supported in insights
};

const BOOLEAN_FILTER_OPERATOR_MAP = {
  true: `is true`,
  false: `is false`,
};

const LIST_FILTER_OPERATOR_MAP = {
  CONTAINS: `contains`,
  DOES_NOT_CONTAIN: `does not contain`,
};

function valueForEvent(event) {
  if (event.startsWith(`$custom_event:`)) {
    const customEventId = Number(event.replace(/^\$custom_event:/, ``));
    return {custom: true, id: customEventId};
  } else {
    return {name: event};
  }
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
function generateParams({query}) {
  query = Object.assign(
    {
      showClauses: [],
      propertyFilters: [],
      groupByProperties: [],
    },
    query,
  );

  const showClauses = query.showClauses.map((showClause) => {
    if (showClause.showEvent) {
      const showEvent = showClause.showEvent;
      return {
        dataGroupId: query.dataGroupId,
        math: MATH_ACTION_MAP[showEvent.action],
        resourceType: PROPERTY_FILTERS_OPERATOR_MAP.AND,
        value: valueForEvent(showEvent.event),
      };
    } else if (showClause.showEventProperty) {
      const showEventProperty = showClause.showEventProperty;
      const property = showEventProperty.property;
      return {
        dataGroupId: query.dataGroupId,
        math: MATH_ACTION_MAP[showEventProperty.action],
        resourceType: PROPERTY_FILTERS_OPERATOR_MAP.AND,
        property: {
          name: property.name,
          resourceType: PROPERTY_SOURCE_MAP[property.source],
          type: PROPERTY_TYPE_MAP[property.type],
        },
        value: valueForEvent(showEventProperty.event),
      };
    } else if (showClause.showPeopleProperty) {
      const showPeopleProperty = showClause.showPeopleProperty;
      const property = showPeopleProperty.property;
      return {
        dataGroupId: query.dataGroupId,
        math: MATH_ACTION_MAP[showPeopleProperty.action],
        resourceType: PROPERTY_SOURCE_MAP.PEOPLE,
        property: {
          name: property.name,
          resourceType: PROPERTY_SOURCE_MAP[property.source],
          type: PROPERTY_TYPE_MAP[property.type],
        },
      };
    } else {
      return {};
    }
  });

  const groupClauses = query.groupByProperties.map((groupByProperty) => {
    const property = groupByProperty.property;
    const groupClause: any = {
      propertyType: PROPERTY_TYPE_MAP[property.type],
      typeCast: property.typecast ? PROPERTY_TYPE_MAP[property.typecast] : null,
      resourceType: PROPERTY_SOURCE_MAP[property.source],
      value: property.name,
    };
    if (groupByProperty.timeUnit) {
      groupClause.unit = TIME_UNIT_MAP[groupByProperty.timeUnit];
    }
    return groupClause;
  });

  const filterClauses = query.propertyFilters
    .map((propertyFilter) => {
      let filterClause = {};
      if (propertyFilter.stringFilter) {
        const {operator, operands} = propertyFilter.stringFilter;
        let filterValue;
        if ([`EQUALS`, `DOES_NOT_EQUAL`].includes(operator)) {
          filterValue = operands;
        } else if ([`CONTAINS`, `DOES_NOT_CONTAIN`].includes(operator)) {
          filterValue = operands[0];
        } else {
          filterValue = null;
        }
        filterClause = Object.assign(filterClause, {
          filterOperator: STRING_FILTER_OPERATOR_MAP[operator],
          filterValue,
        });
      } else if (propertyFilter.numberFilter) {
        const {operator, operand1, operand2} = propertyFilter.numberFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: NUMBER_FILTER_OPERATOR_MAP[operator],
          filterValue: operator === `BETWEEN` ? [operand1, operand2] : operand1,
        });
      } else if (propertyFilter.absoluteDateFilter) {
        const {operator, operand1, operand2} = propertyFilter.absoluteDateFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: ABSOLUTE_DATE_FILTER_OPERATOR_MAP[operator],
          filterValue: operator === `BETWEEN` ? [operand1, operand2] : operand1,
        });
      } else if (propertyFilter.relativeDateFilter) {
        const {operator, operand, timeUnit} = propertyFilter.relativeDateFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: RELATIVE_DATE_FILTER_OPERATOR_MAP[operator],
          filterValue: operand,
          filterDateUnit: TIME_UNIT_MAP[timeUnit],
        });
      } else if (propertyFilter.booleanFilter) {
        const {operand} = propertyFilter.booleanFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: BOOLEAN_FILTER_OPERATOR_MAP[operand],
        });
      } else if (propertyFilter.listFilter) {
        const {operator, operand} = propertyFilter.listFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: LIST_FILTER_OPERATOR_MAP[operator],
          filterValue: operand,
        });
      } else if (propertyFilter.cohortFilter) {
        const {operator, operand} = propertyFilter.cohortFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: LIST_FILTER_OPERATOR_MAP[operator],
          filterValue: [operand],
        });
      } else {
        return {};
      }

      const property = propertyFilter.property;
      filterClause = Object.assign(filterClause, {
        value: property.name,
        filterType: PROPERTY_TYPE_MAP[property.typecast || property.type],
        resourceType: PROPERTY_SOURCE_MAP[property.source],
      });

      return filterClause;
    })
    .filter((x) => x);

  const filterSection: any = {clauses: filterClauses};
  if (query.propertyFiltersOperator) {
    filterSection.determiner = PROPERTY_FILTERS_OPERATOR_MAP[query.propertyFiltersOperator];
  }

  let timeClause;
  if (query.dateRange.type === `since`) {
    timeClause = {
      dateRangeType: query.dateRange.type,
      unit: `day`,
      value: [query.dateRange.from],
    };
  } else if (query.dateRange.type === `on`) {
    timeClause = {
      dateRangeType: query.dateRange.type,
      unit: `hour`,
      value: [query.dateRange.from, query.dateRange.to],
    };
  } else if (query.dateRange.type === `in the last`) {
    timeClause = {
      dateRangeType: query.dateRange.type,
      unit: query.dateRange.window.unit,
      window: query.dateRange.window,
    };
  } else if (query.dateRange.type === `between`) {
    if (query.dateRange.from === START_OF_TODAY || query.dateRange.to === END_OF_TODAY) {
      timeClause = {
        dateRangeType: `since`,
        unit: `hour`,
        value: START_OF_TODAY,
      };
    } else if (query.dateRange.from === START_OF_YESTERDAY || query.dateRange.to === END_OF_YESTERDAY) {
      timeClause = {
        dateRangeType: query.dateRange.type,
        unit: `hour`,
        value: [START_OF_YESTERDAY, END_OF_YESTERDAY],
      };
    } else {
      timeClause = {
        dateRangeType: query.dateRange.type,
        unit: `day`,
        value: [query.dateRange.from, query.dateRange.to],
      };
    }
  } else {
    timeClause = {
      dateRangeType: `in the last`,
      unit: `day`,
      window: {
        unit: `day`,
        value: 30,
      },
    };
  }

  const timeClauses = [timeClause];

  return {
    sections: {
      show: {clauses: showClauses},
      group: {clauses: groupClauses},
      filter: filterSection,
      time: {clauses: timeClauses},
      ...(query.dataGroupId ? {globalDataGroupId: query.dataGroupId} : {}),
    },
  };
}

export function generatePermalink({query, projectId, workspaceId}, absolute = false) {
  // In order to stub/spy on generateParams when generatePermalink is called, we need to access
  // it from a enclosed namespace
  const urlParams = generateParams({query});
  let link = `${getReportUrl(`insights`, {projectId, workspaceId})}#${jsurl.stringify(urlParams)}`;
  if (absolute) {
    const rootURL = `${window.location.protocol}//${window.location.host}`;
    link = rootURL + link;
  }
  return link;
}
